// The planning board (M7, WP7.2): URL state, API calls and TanStack Query options for the
// branch's feasibility run, recomputing it, and changing batches from the board (move,
// resize, allocate to an order line, unallocate). Every response is parsed with the shared
// @enp/domain/api schema (lib/api.ts).

import {
  type BatchAllocateRequest,
  type BatchUpdateRequest,
  batchAllocationsResponseSchema,
  batchDetailSchema,
  feasibilityBoardSchema,
  feasibilityRecomputeResponseSchema,
} from '@enp/domain/api';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';
import { invalidateProduction } from '../production/queries.js';

/**
 * Board keys live under `production`, so everything that changes a batch (planning one on
 * the Production screen, cancelling one on its page) refreshes the board too.
 */
export const planningKeys = {
  all: ['production', 'planning'] as const,
  board: (branch: string | undefined) => ['production', 'planning', 'board', branch ?? ''] as const,
};

/** Order keys (the orders screens and the board's order lines). */
export const orderKeys = {
  all: ['orders'] as const,
};

/** After a batch, allocation or order change: the board, production and orders read again. */
export const invalidatePlanning = (queryClient: QueryClient) =>
  Promise.all([
    invalidateProduction(queryClient),
    queryClient.invalidateQueries({ queryKey: orderKeys.all }),
  ]);

/** The board's state in the URL. */
export const boardSearchSchema = z.object({
  branch: z.uuid().optional().catch(undefined),
  /** `issues`: only days with something to act on. */
  show: z.enum(['issues']).optional().catch(undefined),
  /** A batch or order line to bring into view (from an order's coverage link). */
  focus: z.uuid().optional().catch(undefined),
});
export type BoardSearch = z.infer<typeof boardSearchSchema>;

/** How often an open board asks again (a read recomputes first when anything changed). */
export const BOARD_REFRESH_MS = 30_000;

export const boardQuery = (branch: string | undefined) =>
  queryOptions({
    queryKey: planningKeys.board(branch),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (branch) params.set('branch_id', branch);
      const query = params.size > 0 ? `?${params}` : '';
      return api(`/feasibility/board${query}`, { signal, schema: feasibilityBoardSchema });
    },
    staleTime: 10_000,
    refetchInterval: BOARD_REFRESH_MS,
    retry: false,
  });

/** "Check again now": recomputes the branch's run. */
export const recompute = (branch: string | undefined) =>
  api('/feasibility/recompute', {
    method: 'POST',
    body: branch ? { branch_id: branch } : {},
    schema: feasibilityRecomputeResponseSchema,
  });

/** Moves or resizes a planned batch. */
export const updateBatch = (id: string, body: BatchUpdateRequest) =>
  api(`/production/batches/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    schema: batchDetailSchema,
  });

/** Claims part of a batch's output for an order line (one key per dialog, D-063). */
export const allocate = (batchId: string, body: BatchAllocateRequest, idempotencyKey: string) =>
  api(`/production/batches/${encodeURIComponent(batchId)}/allocations`, {
    method: 'POST',
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    schema: batchAllocationsResponseSchema,
  });

/** Gives a claim back (the output goes to the shelf again). */
export const unallocate = (batchId: string, allocationId: string) =>
  api(
    `/production/batches/${encodeURIComponent(batchId)}/allocations/${encodeURIComponent(allocationId)}`,
    { method: 'DELETE', schema: batchAllocationsResponseSchema },
  );
